import { isBefore } from 'date-fns';
import Link from 'next/link';
import { useEffect, useState } from 'react';

import { AlsoGoing } from '../../components/also-going';
import BreadCrumbs from '../../components/breadcrumbs';
import { ContentItem } from '../../components/content-item';
import Guard from '../../components/guard';
import Sidenav from '../../components/sidenav';
import { Spacer } from '../../components/spacer';
import { PageLoader } from '../../components/spinner';
import { Button } from '../../components/styled/button';
import { InputField, SelectField } from '../../components/styled/input';
import {
  Content,
  ContentHeader,
  HideM,
  Layout,
  Main,
  SidenavLayout,
} from '../../components/styled/layout';
import { Heading } from '../../components/styled/text';
import withAuth from '../../components/with-auth';
import { usePageInput } from '../../hooks/use-page-input';
import { superuser, user } from '../../lib/config/roles-and-permissions';
import { useActivitiesQuery } from '../../lib/graphql';

const breadcrumbs = [
  {
    name: 'Alle activiteiten',
    path: '/activities',
  },
];

function Activities() {
  const { pageInput, handleQuery } = usePageInput(1000);
  const [filter, setFilter] = useState({
    activityType: null,
    showActivities: 'inFuture',
  });
  const [{ fetching, error, data }] = useActivitiesQuery({
    variables: {
      input: pageInput,
      filter,
    },
  });

  function handleFilter(e) {
    const { value, name } = e.target;
    if (value === '') {
      // reset filter
      setFilter({
        ...filter,
        [name]: null,
      });
    } else {
      setFilter({
        ...filter,
        [name]: value,
      });
    }
  }

  function handleBooleanFilter(e) {
    const { value, name } = e.target;
    if (value === 'false' || value === '0') {
      // reset filter
      setFilter({
        ...filter,
        [name]: false,
      });
    } else {
      setFilter({
        ...filter,
        [name]: true,
      });
    }
  }

  return (
    <Main>
      <SidenavLayout>
        <Sidenav />
        <div>
          <ContentHeader>
            <div>
              <Heading size={1}>Activiteiten</Heading>
              <Spacer size={0.125} />
              <BreadCrumbs breadcrumbs={breadcrumbs} />
            </div>
            <Guard
              permissions={superuser}
              render={() => (
                <Link href={`/activities/create`}>
                  <a>
                    <Button>
                      <span className="material-icons">add</span>
                      <Spacer size={0.25} /> <HideM>Activiteit&nbsp;</HideM>
                      toevoegen
                    </Button>
                  </a>
                </Link>
              )}
            />
          </ContentHeader>
          <Content>
            <Layout columns="4fr auto 1fr auto 1fr">
              <InputField
                icon="search"
                placeholder="Zoek tussen de activiteiten..."
                value={pageInput.query}
                onChange={handleQuery}
              />
              <Spacer size={1} />
              <SelectField
                defaultValue="false"
                name="showActivities"
                onChange={handleFilter}
                options={
                  <>
                    <option value="inFuture">Aankomende activiteiten</option>
                    <option value="inPast">Verlopen activiteiten</option>
                    <option value="all">Alle activiteiten</option>
                  </>
                }
              />
              <Spacer size={1} />
              <SelectField
                defaultValue=""
                name="activityType"
                onChange={handleFilter}
                options={
                  <>
                    <option value="">Alle typen activiteiten</option>
                    {data?.activityTypes?.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </>
                }
              ></SelectField>
            </Layout>
            <Spacer size={1} />
            {fetching ? (
              <PageLoader />
            ) : (
              <>
                <Heading size="h2">
                  {filter.showActivities === 'all'
                    ? 'Alle activiteiten'
                    : filter.showActivities === 'inFuture'
                    ? 'Aankomende activiteiten'
                    : 'Verlopen activiteiten'}
                </Heading>
                <Spacer size={0.5} />
                <Layout columns="1fr 1fr 1fr" gap="1.5rem" align="start">
                  {data?.activities?.edges?.map((activity) => (
                    <ContentItem
                      key={activity.id}
                      activity={activity}
                      image={activity.image.url}
                      title={activity.name}
                      date={activity.startDate}
                      tag={activity.type.name}
                      summary={activity.description}
                    >
                      <AlsoGoing users={activity.attendees} />
                    </ContentItem>
                  )) ?? <p>Geen activiteiten</p>}
                </Layout>
              </>
            )}
          </Content>
        </div>
      </SidenavLayout>
    </Main>
  );
}

export default withAuth(Activities, ['activity:read']);
